import { useEffect, useRef, useState, useCallback } from 'react'

export interface PlayerConfig {
  src: string
  title: string
  author: string
  authorUrl?: string
  playUrl?: string
  downloadUrl?: string
  duration: number
  waveform: number[]
  compact?: boolean
}

function fmt(t: number): string {
  if (!isFinite(t) || t < 0) t = 0
  const m = Math.floor(t / 60)
  const s = Math.floor(t % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function Player({ config }: { config: PlayerConfig }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(config.duration || 0)
  const [ready, setReady] = useState(false)

  const peaks = config.waveform?.length ? config.waveform : new Array(120).fill(0.15)

  useEffect(() => {
    const audio = new Audio()
    audio.preload = 'none'
    audio.src = config.src
    audioRef.current = audio

    const onTime = () => setCurrent(audio.currentTime)
    const onMeta = () => setDuration(audio.duration || config.duration || 0)
    const onPlay = () => setPlaying(true)
    const onPause = () => setPlaying(false)
    const onEnd = () => { setPlaying(false); setCurrent(0) }
    const onReady = () => setReady(true)

    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('loadedmetadata', onMeta)
    audio.addEventListener('canplay', onReady)
    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onEnd)

    return () => {
      audio.pause()
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('loadedmetadata', onMeta)
      audio.removeEventListener('canplay', onReady)
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onEnd)
    }
  }, [config.src])

  const toggle = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) audio.play()
    else audio.pause()
  }, [])

  const seek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    if (!audio || !duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    audio.currentTime = ratio * duration
    setCurrent(ratio * duration)
  }, [duration])

  const progress = duration ? current / duration : 0

  return (
    <div className="w-full rounded-2xl border border-neutral-800 bg-neutral-900/60 p-4">
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          aria-label={playing ? 'Pause' : 'Play'}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-900 transition hover:bg-white"
        >
          {playing ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-neutral-100">{config.title}</div>
              {config.authorUrl ? (
                <a href={config.authorUrl} className="truncate text-xs text-neutral-400 hover:text-neutral-200">{config.author}</a>
              ) : (
                <div className="truncate text-xs text-neutral-400">{config.author}</div>
              )}
            </div>
            <div className="shrink-0 font-mono text-xs text-neutral-400">{fmt(current)} / {fmt(duration)}</div>
          </div>

          <div
            onClick={seek}
            className="mt-3 flex h-12 cursor-pointer items-end gap-px"
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(current)}
          >
            {peaks.map((p, i) => {
              const filled = i / peaks.length <= progress
              const h = Math.max(2, Math.round(p * 100))
              return (
                <div
                  key={i}
                  className={`flex-1 rounded-sm ${filled ? 'bg-emerald-400' : 'bg-neutral-700'}`}
                  style={{ height: `${h}%` }}
                />
              )
            })}
          </div>
        </div>

        {config.downloadUrl && (
          <a
            href={config.downloadUrl}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-neutral-700 text-neutral-300 transition hover:border-neutral-500 hover:text-white"
            aria-label="Download"
            title="Download"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </a>
        )}
      </div>

      {!ready && config.compact && (
        <div className="mt-2 text-center text-[10px] uppercase tracking-wide text-neutral-600">zoundbites</div>
      )}
    </div>
  )
}
